import useOnScreen from '@rio-cloud/rio-uikit/hooks/useOnScreen';
import Notification from '@rio-cloud/rio-uikit/Notification';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router';

import ErrorFallback from '#components/error/ErrorFallback.tsx';
import { getTrackingAttributes, TRACKING_ACTIONS, TRACKING_CATEGORIES } from '#configuration/setup/googleAnalytics.ts';
import { useFetchTours } from '#features/tours/hooks/useFetchTours';
import { TourDetailsSidebar } from '#features/tours/sidebar/TourDetailsSidebar.tsx';
import { TourTable } from '#features/tours/table/TourTable.tsx';
import { useFetchDrivers } from '#hooks/useFetchDrivers.ts';
import { useTrackScreenSeen } from '#hooks/useTrackScreenSeen.ts';
import { routes } from '#routes/routes';

export const TourServicePage = () => {
    const navigate = useNavigate();
    const intl = useIntl();
    const ref = useRef(null);
    const onScreen = useOnScreen(ref);

    const { tourId: selectedTourId } = useParams();
    const { tours, isLoadingInitialTours, isFetchingTours, toursError, refetchTours } = useFetchTours();
    const { drivers, refetchDrivers } = useFetchDrivers();

    useTrackScreenSeen(onScreen, {
        category: TRACKING_CATEGORIES.TOURS,
        action: TRACKING_ACTIONS.ON_SCREEN,
        label: 'Show tour service page',
    });

    const selectedTour = useMemo(() => tours.find(tour => tour.id === selectedTourId), [selectedTourId, tours]);
    if (toursError) {
        console.error('Error fetching tours:', toursError);
        return <ErrorFallback trackingCategory={TRACKING_CATEGORIES.TOURS} />;
    }

    if (isLoadingInitialTours && tours.length === 0) {
        return <Spinner />;
    }

    const selectTour = (tourId: string | undefined) => {
        navigate(tourId ? `${routes.TOURSERVICE}/${tourId}` : routes.TOURSERVICE);
    };

    const handleRefresh = async () => {
        await Promise.all([refetchTours(), refetchDrivers()]);
        Notification.info(
            intl.formatMessage({
                id: 'intl-msg:ordercom-cloud-frontend.notification.data.update.success',
                defaultMessage: 'Your data has been updated',
            })
        );
    };

    return (
        <div
            ref={ref}
            {...getTrackingAttributes({
                trigger: 'visibility',
                category: TRACKING_CATEGORIES.TOURS,
                action: TRACKING_ACTIONS.RENDER,
                label: 'Render tour service page',
                value: '',
            })}
        >
            {selectedTour && <TourDetailsSidebar tour={selectedTour} setSelectedTourId={selectTour} />}
            <TourTable
                tours={tours}
                drivers={drivers}
                isFetchingTours={isFetchingTours}
                handleTourRefetch={() => void handleRefresh()}
                selectTour={selectTour}
                selectedTourId={selectedTourId}
            />
        </div>
    );
};
